import {
  subscribeToUserNotifications,
  unsubscribeFromNotifications,
} from "@/lib/utils/notificationUtils";
import useAuthStore from "@/store/authStore";
import { useEffect, useRef, useState } from "react";

/**
 * Hook to subscribe to real-time notifications for the current user
 *
 * @param {Object} options - Configuration options
 * @param {boolean} options.enabled - Whether to enable the subscription (default: true)
 * @param {function} options.onInsert - Callback when a notification is created
 * @param {function} options.onUpdate - Callback when a notification is updated
 * @param {function} options.onDelete - Callback when a notification is deleted
 * @returns {Object} - Subscription status and control functions
 */
export function useNotificationRealtime({
  enabled = true,
  onInsert = () => {},
  onUpdate = () => {},
  onDelete = () => {},
} = {}) {
  const { user } = useAuthStore();
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [error, setError] = useState(null);
  const [channel, setChannel] = useState(null);

  // Use refs to track subscription state and prevent duplicates
  const channelRef = useRef(null);
  const isSubscribingRef = useRef(false);
  const lastUserIdRef = useRef(null);

  useEffect(() => {
    // Reset state when user changes
    if (lastUserIdRef.current !== user?.id) {
      setIsSubscribed(false);
      setError(null);
      setChannel(null);
      lastUserIdRef.current = user?.id;
    }

    const setupSubscription = async () => {
      // Don't set up if disabled, no user, already subscribing, or already have a channel
      if (
        !user?.id ||
        !enabled ||
        isSubscribingRef.current ||
        channelRef.current
      ) {
        return;
      }

      try {
        isSubscribingRef.current = true;
        setError(null);

        console.debug(
          `Setting up notification subscription for user ${user.id}`
        );

        // Subscribe to notifications
        const notificationChannel = await subscribeToUserNotifications(
          user.id,
          (payload) => {
            // Handle new notifications
            try {
              onInsert(payload);
            } catch (err) {
              console.debug("Error in onInsert callback:", err);
            }
          },
          (payload) => {
            // Handle notification updates
            try {
              onUpdate(payload);
            } catch (err) {
              console.debug("Error in onUpdate callback:", err);
            }
          },
          (payload) => {
            // Handle notification deletions
            try {
              onDelete(payload);
            } catch (err) {
              console.debug("Error in onDelete callback:", err);
            }
          }
        );

        // Only set the channel if we got a valid response
        if (notificationChannel) {
          channelRef.current = notificationChannel;
          setChannel(notificationChannel);
          setIsSubscribed(true);
          console.debug(
            `Notification subscription established for user ${user.id}`
          );
        } else {
          console.warn("Failed to create notification channel");
          setIsSubscribed(false);
        }
      } catch (err) {
        console.error("Error in notification subscription:", err);
        setError(err.message || "Failed to subscribe to notifications");
        setIsSubscribed(false);
      } finally {
        isSubscribingRef.current = false;
      }
    };

    // Cleanup function
    const cleanup = () => {
      if (channelRef.current) {
        try {
          console.debug(
            `Cleaning up notification subscription for user ${lastUserIdRef.current}`
          );

          // Use a more defensive cleanup approach
          if (typeof channelRef.current.unsubscribe === "function") {
            channelRef.current.unsubscribe();
          } else {
            unsubscribeFromNotifications(channelRef.current);
          }
        } catch (cleanupErr) {
          // Silently handle cleanup errors to prevent component errors
          console.debug("Notification cleanup completed:", cleanupErr.message);
        } finally {
          // Always reset state even if unsubscribe fails
          channelRef.current = null;
          setIsSubscribed(false);
          setChannel(null);
        }
      }
    };

    if (enabled && user?.id) {
      setupSubscription();
    } else {
      // Clean up if disabled or no user
      cleanup();
    }

    // Cleanup subscription on unmount or user change
    return cleanup;
  }, [user?.id, enabled]); // Remove callback dependencies to prevent unnecessary re-subscriptions

  // Function to manually reconnect if needed
  const reconnect = async () => {
    // Clean up existing subscription first
    if (channelRef.current) {
      try {
        if (typeof channelRef.current.unsubscribe === "function") {
          channelRef.current.unsubscribe();
        } else {
          unsubscribeFromNotifications(channelRef.current);
        }
      } catch (err) {
        console.warn("Error unsubscribing from notifications:", err);
      } finally {
        channelRef.current = null;
        setChannel(null);
        setIsSubscribed(false);
      }
    }

    // Reset subscribing flag
    isSubscribingRef.current = false;

    if (user?.id && enabled) {
      try {
        const newChannel = await subscribeToUserNotifications(
          user.id,
          onInsert,
          onUpdate,
          onDelete
        );

        if (newChannel) {
          channelRef.current = newChannel;
          setChannel(newChannel);
          setIsSubscribed(true);
          setError(null);
        }
      } catch (err) {
        setError(err.message || "Failed to reconnect to notifications");
      }
    }
  };

  return {
    isSubscribed,
    error,
    reconnect,
  };
}
